import { SelectedCompartment } from "~/features/inventory/inventory.slice";
import { NextBinInventory } from "~/redux/reducers/autostore";
import {
  BinDto,
  GetBinResponse,
  InventoryDto,
  NextBinResponse,
  NextEmptyBinResponse
} from "~/types/api";

/**
 * Represents the occupancy status of a bin compartment.
 */
export type CompartmentOccupancy = {
  /** The AutoStore Bin compartment number */
  autostoreCompartmentNumber?: number;

  /** Whether the compartment is empty, i.e. free of inventory. */
  isCompartmentEmpty: boolean;
};

/**
 * Automatically mapped type from InventoryDto.
 * Made to allow other callers to pass in just the binId
 * without needing the full InventoryDto.
 */
type BinWithBinId = {
  bin: {
    binId: string;
  };
};

/**
 * Checks if a specific compartment is empty based on the provided inventory.
 * @param compartment - The bin compartment to check
 * @param inventory - The list of inventory items
 * @returns true if the compartment is empty, false otherwise
 */
const isCompartmentEmpty = (
  compartment: BinDto,
  inventory: BinWithBinId[]
): boolean => {
  if (!inventory || inventory.length === 0) {
    return true;
  }

  return !inventory.some((item) => item.bin.binId === compartment.binId);
};

/**
 * Input types that can be used to generate bin compartment occupancy information.
 */
type BinOccupancyInput =
  | NextBinInventory
  | NextEmptyBinResponse
  | NextBinResponse
  | SelectedCompartment
  | GetBinResponse
  | { compartments: BinDto[]; inventory: InventoryDto[] }
  | null
  | undefined;

/**
 * Generates a list of compartment occupancy statuses from various input types.
 * Returns an empty array if input is null or undefined.
 * A compartment is considered empty if there are no inventory items associated with it.
 *
 * @param input - Can be:
 *   - An object with compartments and inventory arrays
 *   - NextBinInventory object
 *   - NextEmptyBinResponse or NextBinResponse
 *   - SelectedCompartment object
 *   - null or undefined
 */
export function getBinCompartmentOccupancy(
  input: BinOccupancyInput
): CompartmentOccupancy[] {
  if (!input) {
    return [];
  }

  let compartments: BinDto[] | undefined;
  let inventory: BinWithBinId[] | undefined;

  // Extract compartments and inventory based on input type
  if ("compartmentNumber" in input) {
    // SelectedCompartment
    compartments = input.bin?.autostoreBinCompartments;
    inventory = input.bin?.inventory;
  } else if ("nextBinInventory" in input) {
    // NextBinInventory
    compartments = input.nextBinResponse?.autostoreBinCompartments;
    inventory = input.nextBinInventory;
  } else if ("openBinResponse" in input) {
    // NextEmptyBinResponse or NextBinResponse
    compartments = input.autostoreBinCompartments;
    inventory = input.inventory;
  } else if ("compartmentIdsWithInventory" in input) {
    // GetBinResponse
    compartments = input.compartments;
    inventory = input.compartmentIdsWithInventory?.map((compartmentId) => ({
      bin: { binId: compartmentId }
    }));
  } else {
    // Direct compartments and inventory object
    compartments = input.compartments;
    inventory = input.inventory;
  }

  if (!compartments) {
    return [];
  }

  return compartments.map((compartment) => ({
    autostoreCompartmentNumber: compartment.autostoreCompartmentNumber,
    isCompartmentEmpty: isCompartmentEmpty(compartment, inventory ?? [])
  }));
}

/**
 * Checks if a specific compartment is empty based on the compartment occupancy list.
 * @param emptyBinResponse - The next empty bin response containing compartment information
 * @param compartmentIndex - Zero-based index of the compartment to check (0-3)
 * @returns true if the compartment is empty, false otherwise
 */
export function isBinResponseCompartmentEmpty(
  emptyBinResponse: NextEmptyBinResponse | GetBinResponse | null | undefined,
  compartmentIndex: number
): boolean {
  if (!emptyBinResponse) {
    return true;
  }

  // Handle different response types
  const compartments =
    "autostoreBinCompartments" in emptyBinResponse
      ? emptyBinResponse.autostoreBinCompartments
      : emptyBinResponse.compartments;

  if (!compartments?.length) {
    return true;
  }

  // Note: 'compartmentIndex' is numbered 0-3 while 'autostoreCompartmentNumber' is 1-4
  const compartment = compartments.find(
    (binCompartment) =>
      binCompartment.autostoreCompartmentNumber === compartmentIndex + 1
  );

  if (!compartment) {
    return true;
  }

  const inventory =
    "inventory" in emptyBinResponse
      ? emptyBinResponse.inventory
      : emptyBinResponse.compartmentIdsWithInventory?.map((compartmentId) => ({
          bin: { binId: compartmentId }
        }));

  return isCompartmentEmpty(compartment, inventory ?? []);
}
